
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Evaluate a manually entered account using Plaid network data',
  preloadImages: [{
    src: '/assets/img/docs/docs-link-overview.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Database Auth' subtitle='Evaluate a manually entered account using Plaid network data
' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Database Auth can increase conversion by providing instant account verification when end users cannot, or do not want to, log into their financial institution to verify their account. Examples include end users at banks that are not supported by Plaid, or users who need to verify a business bank account but who don't have access to manage the account via online banking. With Database Auth, users can instead enter their account and routing number manually. `}</p>
    <Image src='/assets/img/docs/auth/database-auth.png' alt='Link flow. First screen shows Plaid consent pane, second screen shows radio toggle option to connect bank instantly (recommended) or manually, manually option is selected. Third screen shows a form where user can enter account details: account and routing number, owner name, and checking or savings.' caption='Example Link flow with Database Auth' expandable={true} mdxType="Image" />
    <p>{`Database Auth verifies account and routing numbers by checking the information provided against Plaid's known account numbers, leveraging Plaid's database of over 200 million verified accounts. If no match is found, Plaid will check the account number format against known usages by the institution associated with the given routing number. Database Auth will provide a verification status of 'pass', 'pass with caution', or 'fail' and a set of attributes that contributed to that status, such as whether a match was found or whether Plaid fell back to checking account number formats. `}</p>
    <p>{`Database Auth does not verify that the user has access to the bank account and also does not fully guarantee that the account exists. Database Auth does not create a live connection to the bank account and cannot be used to check real-time balances (although it is compatible with anti-risk products such as Signal and Identity Match). For these reasons, Database Auth should not be enabled where there is a very high risk of fraud or ACH returns. `}</p>
    <p>{`You can try out the Database Auth flow in an `}<a parentName="p" {...{
        "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100&step=7"
      }}>{`Interactive Demo`}</a>{`. `}</p>
    <h5 {...{
      "id": "geographic-availability"
    }}>{`Geographic availability`}</h5>
    <p>{`Database Auth is available in the United States and Canada. `}</p>
    <p>{`In Canada, name match scores are not available, and users cannot fall back to the micro-deposit flow.`}</p>
    <h5 {...{
      "id": "database-auth-flow"
    }}>{`Database Auth flow`}</h5>
    <ol>
      <li parentName="ol">{`Starting on a page in your app, the user clicks an action that opens Plaid Link.`}</li>
      <li parentName="ol">{`Inside of Plaid Link, the user selects an option that triggers the manual verification flow. This could occur because the user opted in to the manual flow (if Auth Type Select is enabled) or because their institution does not support Instant Auth.`}</li>
      <li parentName="ol">{`The user will be prompted to enter their name, routing number, and account number.`}</li>
      <li parentName="ol">{`Based on rules you have configured, the user will see a success screen or a failure screen, or be prompted to fall back to micro-deposit-based verification.`}</li>
    </ol>
    <h4 {...{
      "id": "implementation-steps"
    }}>{`Implementation steps`}</h4>
    <ol>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` with the following parameters:`}
        <ul parentName="li">
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array should include `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{` only, and no other products. `}</li>
          <li parentName="ul">{`If you are enabling Identity Match, put `}<inlineCode parentName="li">{`identity`}</inlineCode>{` in the `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` array, as putting it in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array will prevent Database Auth from activating. Approximately one-third of all Items verified via Database Auth are compatible with Identity Match.`}</li>
          <li parentName="ul">{`If you plan to use Signal Transaction Scores, put `}<inlineCode parentName="li">{`signal`}</inlineCode>{` in the `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` or `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` array. Putting it in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array will prevent Database Auth from activating. Approximately one-third of all Items verified via Database Auth are compatible with Signal Transaction Scores.`}</li>
          <li parentName="ul">{`No other products besides Signal Transaction Scores and Identity Match are compatible with Database Auth Items. To enable other products for Items where Database Auth is not used, put those products in the `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` array. `}</li>
          <li parentName="ul"><inlineCode parentName="li">{`country_codes`}</inlineCode>{` should be set to `}<inlineCode parentName="li">{`['US']`}</inlineCode>{` or `}<inlineCode parentName="li">{`['CA']`}</inlineCode>{` – Database Auth is currently available in the United States and Canada.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`In the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/account-verification"
        }}>{`Dashboard Account Verification pane`}</a>{`, ensure "Manage via Dashboard" is selected, and enable "Verify with Database Auth".`}</li>
      <li parentName="ol">{`Configure the rules:`}
        <ul parentName="li">
          <li parentName="ul">{`(Recommended for most customers) To use Plaid's rules-based, customizable, no-code evaluation logic, select "For certain Database Auth results, fallback".`}</li>
          <li parentName="ul">{`To use your own evaluation logic, select "For any Database Auth result, complete Link". This option is designed for customers who require more fine-grained control of the Link flow beyond what the ruleset configuration allows, or who have highly sophisticated fraud evaluation systems and require more custom logic. See `}<a parentName="li" {...{
              "href": "#using-your-own-evaluation-logic-advanced"
            }}>{`Using your own evaluation logic`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Open Link`}</a>{` according to the instructions for your platform, using the `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` created earlier. `}</li>
      <li parentName="ol">{`After the end user completes the Link session, if the session was successful, or if you are using manual evaluation logic, Link will return a `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` that you can exchange for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{`. You can then proceed to call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{` and/or `}<inlineCode parentName="li">{`/processor/auth/get`}</inlineCode>{`, as described in the main `}<a parentName="li" {...{
          "href": "/docs/auth/#integration-process"
        }}>{`Auth integration process`}</a>{`.`}</li>
      <li parentName="ol">{`If the session was unsuccessful, Link will fall back to either an error screen or to a micro-deposit flow, depending on which options you selected in the Account Verification pane.`}</li>
    </ol>
    <h4 {...{
      "id": "backend-only-implementation-steps"
    }}>{`Backend-only implementation steps`}</h4>
    <Callout mdxType="Callout">
      <p>{`The backend-only `}<inlineCode parentName="p">{`/auth/verify`}</inlineCode>{`-based verification process for Database Auth is in early availability. To request access, contact sales or your Plaid account manager.`}</p>
    </Callout>
    <p>{`Database Auth can be used to verify account numbers you have already collected without the Plaid Link flow. Call `}<inlineCode parentName="p">{`/auth/verify`}</inlineCode>{` and provide the account and routing number and, optionally, the account owner name. The response will include the same Database Auth-related results as `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`: the verification status, name match score (if a name was provided in the request and name matching data is available), and verification insights. For more details, see the `}<inlineCode parentName="p">{`/auth/verify`}</inlineCode>{` API reference. `}</p>
    <h4 {...{
      "id": "understanding-verification-status-and-name-match-score"
    }}>{`Understanding verification status and name match score`}</h4>
    <p>{`Your business logic will be based on the results of the verification status and the name match score.`}</p>
    <h5 {...{
      "id": "verification-status"
    }}>{`Verification status`}</h5>
    <p>{`The possible values and most common causes for `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#auth-get-response-accounts-verification-status"
      }}><inlineCode parentName="a">{`verification_status`}</inlineCode></a>{` for an Item going through Database Auth are:`}</p>
    <p><inlineCode parentName="p">{`database_insights_pass`}</inlineCode>{`: Plaid was able to match the account and routing number provided to an existing Item on the Plaid network and has high confidence that the account exists. The account is not known to be closed or frozen.`}</p>
    <p><inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{`: Plaid was unable to match the account and routing number provided to an existing Item on the Plaid network, but the routing number is valid and the account number matches Plaid's known rules for account number formatting at the associated financial institution.`}</p>
    <p><inlineCode parentName="p">{`database_insights_fail`}</inlineCode>{`: The routing number is not recognized, or the account number does not appear to match the formatting rules at the associated financial institution, or Plaid has received data indicating the account is closed or frozen. `}</p>
    <p>{`In addition to the common causes above, additional risk factors may impact `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` results.`}</p>
    <Callout mdxType="Callout">
      <p>{`Balance is not compatible with Items verified via Database Auth. If you need to check for potential ACH return risk for items verified via Database Auth, you must use Signal Transaction Scores. `}</p>
    </Callout>
    <h6 {...{
      "id": "understanding-the-pass-with-caution-verification-status"
    }}>{`Understanding the pass with caution verification status`}</h6>
    <p>{`Because `}<inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{` is a common verification result, how you choose to handle it is important.`}</p>
    <p>{`In a typical test scenario, accepting only `}<inlineCode parentName="p">{`database_insights_pass`}</inlineCode>{` results reduced administrative return rates by over 80%, while accepting both `}<inlineCode parentName="p">{`database_insights_pass`}</inlineCode>{` and `}<inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{` reduced administrative return rates by approximately 50%. Note that results will vary based on your own user base and use case and are not guarantees. `}</p>
    <p>{`If you do not want to accept `}<inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{`, you can configure Link to use a `}<a parentName="p" {...{
        "href": "#fallback-methods"
      }}>{`Fallback method`}</a>{`.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Signal Transaction Scores and Identity Match are only compatible with Database Auth Items whose verification status is `}<inlineCode parentName="p">{`database_insights_pass`}</inlineCode>{`. If you need to use Signal Transaction Scores to check for non-administrative ACH return risk (e.g. R01 "insufficient funds" returns), or need to check for account takeover fraud using Identity Match, you should not accept the `}<inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{` status. `}</p>
    </Callout>
    <h5 {...{
      "id": "name-match-score"
    }}>{`Name match score`}</h5>
    <p>{`The possible values for the name match score range from 0-100. In general, you should require a name match of at least 70 to effectively protect against account takeover attacks. A name match score will only be returned if the session configuration was enabled for name matching and a name could be found to match against.`}</p>
    <h6 {...{
      "id": "example-of-how-to-interpret-name-match-score"
    }}>{`Example of how to interpret name match score`}</h6>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Range`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Meaning`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Example`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`100`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Exact match`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Jennifer Smith, Jennifer Smith`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`99`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Exact match after normalization`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Jennifer Smith, Ms. Jennifer Smith, Jennifer W. Smith; Li Chen, Chen Li`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`95-98`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Strong match`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Andrew Smith, Andrew Smith Jr.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`90-94`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Strong match, likely single typo or nickname, or missing portion of Spanish surnames`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Jennifer Smith, Jen Smith, Jennfer Smith; Alejandro García Rodriguez Santos, Alejandro Garcia Rodriguez`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`70-89`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Likely match, likely abbreviated / missing portion of name, or multiple typos`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Jennifer Smith, J Smith, Jennifer Williams Smith, Jennfer Smth`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`50-69`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Possible match, likely relative or missing portion of non-Spanish surnames`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Mohammed Naseer Daoud Mohammed, Mohammed Nasser Daoud; Jennifer Smith, Betty Smith`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`0-49`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unlikely match`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Jennifer Smith, Ray Charles`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "using-your-own-evaluation-logic-advanced"
    }}>{`Using your own evaluation logic (advanced)`}</h5>
    <p>{`If you selected "For any Database Auth result, complete Link", you must write your own code for handling the results of the Database Auth verification.`}</p>
    <p>{`To get the results, call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` and examine the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{`, `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{`, and `}<inlineCode parentName="p">{`verification_name`}</inlineCode>{` fields. Based on the values in these fields, you will make a decision on whether to accept the account data as verified, take additional risk mitigation steps, or reject the account data as unverified.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{` object will contain more detailed analysis showing exactly which checks passed or failed to result in the given `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{`. It will also contain the `}<inlineCode parentName="p">{`name_match_score`}</inlineCode>{`. `}</p>
    <p>{`For more details on the `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{` values, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#auth-get-response-accounts-verification-insights"
      }}>{`API Reference`}</a>{`.`}</p>
    <p>{`When using your own evaluation logic, you can fallback only to Automated Micro-deposits, and not to Instant Micro-deposits or Same-Day Micro-deposits.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`When using other verification methods, customers using a `}<a parentName="p" {...{
          "href": "/docs/auth/partnerships"
        }}>{`processor partner`}</a>{` do not typically need to call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, and can directly call `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` instead. However, if you are using Database Auth with a processor partner and using your own evaluation logic, you must call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` and check the value of the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`verification_insights`}</inlineCode>{` fields before passing a processor token to the partner. `}</p>
    </Callout>
    <h5 {...{
      "id": "fallback-methods"
    }}>{`Fallback methods`}</h5>
    <p>{`If Database Auth fails, you can configure fallback to `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/instant/#instant-micro-deposits"
      }}>{`Instant Micro-deposits`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day/"
      }}>{`Same-Day Micro-deposits`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/automated"
      }}>{`Automated Micro-deposits`}</a>{` via the Account Verification Dashboard (US only).`}</p>
    <h4 {...{
      "id": "testing-database-auth-in-sandbox"
    }}>{`Testing Database Auth in Sandbox`}</h4>
    <p>{`For test credentials that can be used to test Database Insights in the Sandbox environment, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing#testing-database-auth-or-database-insights"
      }}>{`Testing Database Auth`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "implementation-steps",
        level: 4,
        title: "Implementation steps",
      },
{
        id: "backend-only-implementation-steps",
        level: 4,
        title: "Backend-only implementation steps",
      },
{
        id: "understanding-verification-status-and-name-match-score",
        level: 4,
        title: "Understanding verification status and name match score",
      },
{
        id: "testing-database-auth-in-sandbox",
        level: 4,
        title: "Testing Database Auth in Sandbox",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
